import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelText as _vModelText, withModifiers as _withModifiers, withKeys as _withKeys, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = ["stroke"]
const _hoisted_2 = ["dir"]
const _hoisted_3 = ["placeholder", "onKeydown"]
const _hoisted_4 = ["stroke"]
const _hoisted_5 = ["onClick", "onMouseover"]

import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

import { useRTL } from '@/composables/useRTL';

import { RawPlace } from '@/models/sidebar/places.model';


export default /*@__PURE__*/_defineComponent({
  __name: 'Search',
  props: {
  places: { type: Array as () => RawPlace[], required: true },
  isRtl: { type: Boolean, default: false }
},
  emits: ['place-selected'],
  setup(__props, { emit: __emit }) {

const store = useStore();
const { t } = useI18n();
const rtl = useRTL();

// eslint-disable-next-line no-undef
const props = __props;

// eslint-disable-next-line no-undef
const emit = __emit;

const isNavbarMenuOpen = computed(() => store.state.mobileMenu);

const searchContainer = ref<HTMLElement | null>(null);
const searchQuery = ref('');
const isExpanded = ref(false);
const isFocused = ref(false);
const showDropdown = ref(false);
const highlightedIndex = ref(-1);
const dropdownRef = ref<HTMLElement | null>(null);

watch(searchQuery, () => {
  highlightedIndex.value = -1;
});

const getLocalizedValue = (match: RawPlace, baseKey: string): string => {
  const localeKey = `${baseKey}_${store.state.locale}`;
  const safeMatch = match as Record<string, any>;

  return safeMatch[localeKey] || safeMatch[baseKey] || '';
};

const isValidLocation = (location: any): boolean => {
  if (!location) {
    return false;
  }

  if (Array.isArray(location) && location.length >= 2) {
    return !(Number(location[0]) === 0 && Number(location[1]) === 0);
  }

  if (typeof location === 'object' && !Array.isArray(location)) {
    return !(Number(location.lat || 0) === 0 && Number(location.lng || 0) === 0);
  }

  return true;
};

const filteredMatches = computed(() => {
  const query = normalizeText(searchQuery.value);

  if (!query) {
    return [];
  }

  return props.places
    .filter(p => isValidLocation(p.location) && normalizeText(getLocalizedValue(p, 'place')).includes(query))
    .slice(0, 15);
});

const normalizeText = (text: string) => {
  if (!text) {
    return '';
  }
  return text.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
};

const toggleExpand = () => {
  isExpanded.value = true;
  setTimeout(() => {
    const input = searchContainer.value?.querySelector('input');
    if (input) {
      input.focus();
    }
  }, 50);
};

const handleFocus = () => {
  isFocused.value = true;
  if (searchQuery.value.trim().length > 0) {
    showDropdown.value = true;
  }
};

const handleBlur = () => {
  isFocused.value = false;
};

const clearSearch = () => {
  searchQuery.value = '';
  showDropdown.value = false;
  const input = searchContainer.value?.querySelector('input');
  if (input) {
    input.focus();
  }
};

const selectPlace = (place: RawPlace) => {
  searchQuery.value = getLocalizedValue(place, 'place');
  showDropdown.value = false;
  isExpanded.value = false;
  emit('place-selected', place);
};

const navigateDown = () => {
  if (!showDropdown.value || filteredMatches.value.length === 0) {
    return;
  }

  if (highlightedIndex.value < filteredMatches.value.length - 1) {
    highlightedIndex.value++;
  }
  else {
    highlightedIndex.value = 0;
  }

  scrollToHighlighted();
};

const navigateUp = () => {
  if (!showDropdown.value || filteredMatches.value.length === 0) {
    return;
  }

  if (highlightedIndex.value > 0) {
    highlightedIndex.value--;
  }
  else {
    highlightedIndex.value = filteredMatches.value.length - 1;
  }

  scrollToHighlighted();
};

const executeSearch = () => {
  if (showDropdown.value && highlightedIndex.value >= 0 && filteredMatches.value[highlightedIndex.value]) {
    selectPlace(filteredMatches.value[highlightedIndex.value]);
    return;
  }

  const query = normalizeText(searchQuery.value);
  if (!query) {
    return;
  }

  const match = props.places.find(p => isValidLocation(p.location) && normalizeText(getLocalizedValue(p, 'place')).includes(query));
  if (match) {
    selectPlace(match);
  }
  else {
    showDropdown.value = false;
  }
};

const scrollToHighlighted = async () => {
  await nextTick();

  if (!dropdownRef.value) {
    return;
  }
  const container = dropdownRef.value;
  const element = container.children[highlightedIndex.value] as HTMLElement;

  if (!element) {
    return;
  }

  const containerTop = container.scrollTop;
  const containerBottom = containerTop + container.clientHeight;
  const elementTop = element.offsetTop;
  const elementBottom = elementTop + element.offsetHeight;

  if (elementTop < containerTop) {
    container.scrollTop = elementTop;
  }
  else if (elementBottom > containerBottom) {
    container.scrollTop = elementBottom - container.clientHeight;
  }
};

const handleClickOutside = (e: MouseEvent) => {
  if (searchContainer.value && !searchContainer.value.contains(e.target as Node)) {
    showDropdown.value = false;
    isExpanded.value = false;
  }
};

onMounted(() => document.addEventListener('click', handleClickOutside));
onUnmounted(() => document.removeEventListener('click', handleClickOutside));

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["map-search-container", { 'with-sidebar': _unref(store).state.view === 'sidebar', 'expanded': isExpanded.value, 'rtl-layout': __props.isRtl, 'shifted-down': isNavbarMenuOpen.value, 'dark-mode': _ctx.$store.state.dark }]),
    ref_key: "searchContainer",
    ref: searchContainer
  }, [
    _createElementVNode("div", {
      class: "search-toggle-btn",
      onClick: toggleExpand
    }, [
      (_openBlock(), _createElementBlock("svg", {
        viewBox: "0 0 24 24",
        width: "20",
        height: "20",
        stroke: _ctx.$store.state.dark ? '#E6EAF2' : '#2c3e50',
        "stroke-width": "2",
        fill: "none",
        "stroke-linecap": "round",
        "stroke-linejoin": "round"
      }, _cache[2] || (_cache[2] = [
        _createElementVNode("circle", {
          cx: "11",
          cy: "11",
          r: "8"
        }, null, -1),
        _createElementVNode("line", {
          x1: "21",
          y1: "21",
          x2: "16.65",
          y2: "16.65"
        }, null, -1)
      ]), 8, _hoisted_1))
    ]),
    _createElementVNode("div", {
      class: _normalizeClass(["search-input-wrapper", { 'focused': isFocused.value, 'rtl': _unref(rtl) }]),
      dir: _unref(rtl) ? 'rtl' : 'ltr'
    }, [
      _withDirectives(_createElementVNode("input", {
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
        type: "text",
        class: "map-search-input",
        placeholder: _unref(t)('nav.search_places'),
        spellcheck: "false",
        autocomplete: "off",
        onFocus: handleFocus,
        onBlur: handleBlur,
        onKeydown: [
          _withKeys(_withModifiers(navigateDown, ["prevent"]), ["down"]),
          _withKeys(_withModifiers(navigateUp, ["prevent"]), ["up"]),
          _withKeys(_withModifiers(executeSearch, ["prevent"]), ["enter"])
        ],
        onInput: _cache[1] || (_cache[1] = ($event: any) => (showDropdown.value = true))
      }, null, 40, _hoisted_3), [
        [_vModelText, searchQuery.value]
      ]),
      (searchQuery.value)
        ? (_openBlock(), _createElementBlock("span", {
            key: 0,
            class: "search-clear-btn",
            onClick: clearSearch
          }, "×"))
        : _createCommentVNode("", true),
      _createElementVNode("span", {
        class: "search-submit-btn",
        onClick: executeSearch
      }, [
        (_openBlock(), _createElementBlock("svg", {
          viewBox: "0 0 24 24",
          width: "16",
          height: "16",
          stroke: _ctx.$store.state.dark ? '#E6EAF2' : '#2c3e50',
          "stroke-width": "2",
          fill: "none",
          "stroke-linecap": "round",
          "stroke-linejoin": "round"
        }, _cache[3] || (_cache[3] = [
          _createElementVNode("circle", {
            cx: "11",
            cy: "11",
            r: "8"
          }, null, -1),
          _createElementVNode("line", {
            x1: "21",
            y1: "21",
            x2: "16.65",
            y2: "16.65"
          }, null, -1)
        ]), 8, _hoisted_4))
      ])
    ], 10, _hoisted_2),
    (showDropdown.value && filteredMatches.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "search-dropdown",
          ref_key: "dropdownRef",
          ref: dropdownRef
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredMatches.value, (match, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: match._id,
              class: _normalizeClass(["dropdown-item", { 'keyboard-highlighted': index === highlightedIndex.value }]),
              onClick: ($event: any) => (selectPlace(match)),
              onMouseover: ($event: any) => (highlightedIndex.value = index)
            }, _toDisplayString(getLocalizedValue(match, 'place')) + ", " + _toDisplayString(getLocalizedValue(match, 'district')), 43, _hoisted_5))
          }), 128))
        ], 512))
      : _createCommentVNode("", true)
  ], 2))
}
}

})